/**
 * This file is called `styled` not `styled-components` so there is no conflict between the module and this file
 */
import { ShouldForwardProp } from 'styled-components';
import validProp from '@emotion/is-prop-valid';

/**
 * Sometimes there are props which have a valid HTML attribute name but are we do not want them to be passed through
 * to the DOM element. An example of this is `width` when IHasWidth is used as we don't want the object to be output onto
 * the DOM element
 *
 * When this is the case, we need to tell styled component to remove these props. This can be done by adding the
 * following to the styled component
 *
 * `styled.div.withConfig({ shouldForwardProp: shouldForwardProp(['width']) })`
 */
export const shouldForwardProp =
  <T>(propsToExclude: keyof T | Array<keyof T>): ShouldForwardProp<'web'> =>
  (prop, element) =>
    !(Array.isArray(propsToExclude) ? propsToExclude : [propsToExclude]).includes(prop as keyof T) &&
    isValidProp(prop, element);

/**
 * We need to ensure that we are only removing nonvalid HTML attribute from HTML DOM elements. We still need
 * to pass through any props to an React components so we check if the element is a string, if so, this is a DOM element
 */
export const isValidProp: ShouldForwardProp<'web'> = (prop, element) => {
  const isDomElement = typeof element === 'string';
  return !isDomElement || validProp(prop);
};
